import { useState, useRef, useEffect } from 'react';

export interface Salesperson {
  id: string;
  name: string;
  employeeId: string;
  status: 'active' | 'inactive';
  assignedStore: string;
  commissionType: 'fixed' | 'tier' | 'target';
  commissionRate: number;
  monthlyTarget: number;
  dailyTarget?: number;
  targetPeriod: 'monthly' | 'weekly';
  currentMonthSales: number;
  currentDaySales: number;
  commissionEarned: number;
}

interface SalespersonSelectorProps {
  selectedSalesperson: Salesperson | null;
  onSelect: (salesperson: Salesperson | null) => void;
  disabled?: boolean;
  required?: boolean;
  userRole: 'cashier' | 'supervisor' | 'manager';
}

// Mock salesperson data
const mockSalespersons: Salesperson[] = [
  {
    id: 'SP001',
    name: 'Michael Chen',
    employeeId: 'EMP-2401',
    status: 'active',
    assignedStore: 'Downtown Store',
    commissionType: 'fixed',
    commissionRate: 2.5,
    monthlyTarget: 50000,
    dailyTarget: 2000,
    targetPeriod: 'monthly',
    currentMonthSales: 42350,
    currentDaySales: 1850,
    commissionEarned: 1058.75,
  },
  {
    id: 'SP002',
    name: 'Jennifer Martinez',
    employeeId: 'EMP-2402',
    status: 'active',
    assignedStore: 'Downtown Store',
    commissionType: 'target',
    commissionRate: 3.0,
    monthlyTarget: 60000,
    dailyTarget: 2500,
    targetPeriod: 'monthly',
    currentMonthSales: 65200,
    currentDaySales: 3100,
    commissionEarned: 1956.00,
  },
  {
    id: 'SP003',
    name: 'David Thompson',
    employeeId: 'EMP-2403',
    status: 'active',
    assignedStore: 'Downtown Store',
    commissionType: 'tier',
    commissionRate: 2.0,
    monthlyTarget: 40000,
    targetPeriod: 'monthly',
    currentMonthSales: 38900,
    currentDaySales: 1650,
    commissionEarned: 778.00,
  },
  {
    id: 'SP004',
    name: 'Emily Rodriguez',
    employeeId: 'EMP-2404',
    status: 'active',
    assignedStore: 'Downtown Store',
    commissionType: 'fixed',
    commissionRate: 2.0,
    monthlyTarget: 45000,
    dailyTarget: 1800,
    targetPeriod: 'monthly',
    currentMonthSales: 51200,
    currentDaySales: 2200,
    commissionEarned: 1024.00,
  },
  {
    id: 'SP005',
    name: 'Robert Kim',
    employeeId: 'EMP-2405',
    status: 'active',
    assignedStore: 'Downtown Store',
    commissionType: 'target',
    commissionRate: 2.5,
    monthlyTarget: 55000,
    dailyTarget: 2200,
    targetPeriod: 'monthly',
    currentMonthSales: 48300,
    currentDaySales: 1900,
    commissionEarned: 0, // Below target
  },
];

export default function SalespersonSelector({ 
  selectedSalesperson, 
  onSelect, 
  disabled = false,
  required = false,
  userRole 
}: SalespersonSelectorProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Check if user has permission to change salesperson
  const canChangeSalesperson = userRole === 'supervisor' || userRole === 'manager';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current && 
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredSalespersons = mockSalespersons.filter(sp => 
    sp.status === 'active' && (
      sp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sp.employeeId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sp.id.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const handleSelect = (salesperson: Salesperson) => {
    onSelect(salesperson);
    setShowDropdown(false);
    setSearchQuery('');
  };

  const handleClear = () => {
    if (!required) {
      onSelect(null);
      setShowDropdown(false);
    }
  };

  const getTargetProgress = (sp: Salesperson) => {
    const progress = (sp.currentMonthSales / sp.monthlyTarget) * 100;
    return Math.min(progress, 100);
  };

  const getCommissionTypeLabel = (type: string) => {
    switch (type) {
      case 'fixed': return 'Fixed %';
      case 'tier': return 'Tier Based';
      case 'target': return 'Target Based';
      default: return type;
    }
  };

  return (
    <div className="relative">
      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
        Salesperson {required && <span className="text-red-500">*</span>}
      </label>
      
      <button
        ref={buttonRef}
        onClick={() => !disabled && canChangeSalesperson && setShowDropdown(!showDropdown)}
        disabled={disabled || !canChangeSalesperson}
        className={`w-full px-3 py-2.5 bg-white border-2 rounded-lg text-left flex items-center justify-between transition-all ${
          disabled || !canChangeSalesperson
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
            : showDropdown
            ? 'border-teal-500 shadow-sm'
            : 'border-gray-300 hover:border-gray-400 cursor-pointer'
        } ${required && !selectedSalesperson ? 'border-red-300' : ''}`}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-8 h-8 flex items-center justify-center bg-teal-100 text-teal-700 rounded-full flex-shrink-0">
            <i className="ri-user-star-line text-lg"></i>
          </div>
          {selectedSalesperson ? (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {selectedSalesperson.name}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {selectedSalesperson.employeeId} • {getCommissionTypeLabel(selectedSalesperson.commissionType)} {selectedSalesperson.commissionRate}%
              </p>
            </div>
          ) : (
            <div className="flex-1">
              <p className="text-sm text-gray-500">
                {required ? 'Select salesperson (required)' : 'No salesperson assigned'}
              </p>
            </div>
          )}
        </div>
        {canChangeSalesperson && !disabled && (
          <i className={`ri-arrow-${showDropdown ? 'up' : 'down'}-s-line text-gray-400 text-lg flex-shrink-0`}></i>
        )}
      </button>

      {!canChangeSalesperson && (
        <p className="text-xs text-amber-600 mt-1">
          <i className="ri-lock-line mr-1"></i>
          Only supervisors and managers can assign salesperson
        </p>
      )}

      {showDropdown && canChangeSalesperson && (
        <div 
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-teal-500 rounded-lg shadow-2xl z-50 max-h-96 overflow-hidden flex flex-col"
        >
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or ID..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-teal-500"
                autoFocus
              />
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {!required && (
              <button
                onClick={handleClear}
                className="w-full p-3 text-left hover:bg-gray-50 border-b border-gray-100 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-100 text-gray-400 rounded-full flex-shrink-0">
                    <i className="ri-user-unfollow-line text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">No Salesperson</p>
                    <p className="text-xs text-gray-500">Clear assignment</p>
                  </div>
                </div>
              </button>
            )}

            {filteredSalespersons.length === 0 ? (
              <div className="p-6 text-center">
                <i className="ri-user-search-line text-3xl text-gray-300 mb-2"></i>
                <p className="text-sm text-gray-600">No salespersons found</p>
              </div>
            ) : (
              filteredSalespersons.map((sp) => {
                const targetProgress = getTargetProgress(sp);
                const isTargetMet = sp.currentMonthSales >= sp.monthlyTarget;
                
                return (
                  <button
                    key={sp.id}
                    onClick={() => handleSelect(sp)}
                    className={`w-full p-3 text-left hover:bg-teal-50 border-b border-gray-100 transition-colors cursor-pointer ${
                      selectedSalesperson?.id === sp.id ? 'bg-teal-50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-teal-500 to-teal-600 text-white rounded-full flex-shrink-0 font-semibold text-sm">
                        {sp.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-semibold text-gray-900">{sp.name}</p>
                          {isTargetMet && (
                            <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium whitespace-nowrap">
                              <i className="ri-trophy-line mr-0.5"></i>Target Met
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 mb-2">
                          {sp.employeeId} • {getCommissionTypeLabel(sp.commissionType)} {sp.commissionRate}%
                        </p>
                        
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-600">Monthly Target</span>
                            <span className="font-semibold text-gray-900">
                              ${sp.currentMonthSales.toLocaleString()} / ${sp.monthlyTarget.toLocaleString()}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all ${
                                isTargetMet ? 'bg-green-500' : 'bg-teal-500'
                              }`}
                              style={{ width: `${targetProgress}%` }}
                            ></div>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-600">Today: ${sp.currentDaySales.toLocaleString()}</span>
                            <span className="text-teal-600 font-semibold">
                              Commission: ${sp.commissionEarned.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
